// Server-safe component - no "use client" directive
import React from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { mmddyyyy } from "@prescreen/lib/date-utils"
import { dollars, social } from "@prescreen/lib/formats"
import { EquifaxPrequalReport } from "@prescreen/lib/equifax-prequal-report"
import { CreditEvaluation } from "@prescreen/lib/types/credit-evaluation"
import TradeAccount from "./PrequalReport/TradeAccount"
import { EvaluationsSectionServer } from "./EvaluationsSectionServer"
import "./EquifaxReportView.css"

interface EquifaxReportViewContentProps {
  report: EquifaxPrequalReport | null
  evaluations?: CreditEvaluation[]
  rawReport?: unknown
  useServerEvaluations?: boolean
  evaluationsComponent?: React.ReactNode
  reportTitle?: string
  bureau?: string
}

export function EquifaxReportViewContent({ 
  report, 
  evaluations, 
  rawReport,
  useServerEvaluations = false,
  evaluationsComponent,
  reportTitle,
  bureau
}: EquifaxReportViewContentProps) {
  if (!report) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Report Data</CardTitle>
          <CardDescription>Unable to parse report data</CardDescription>
        </CardHeader>
        <CardContent>
          <pre className="bg-gray-100 p-4 rounded text-xs overflow-auto max-h-96">
            {JSON.stringify(rawReport, null, 2)}
          </pre>
        </CardContent>
      </Card>
    )
  }

  const getBureauLabel = (b: string): string => {
    const labels: Record<string, string> = {
      equifax: 'Equifax',
      experian: 'Experian',
      transunion: 'TransUnion'
    }
    return labels[b] || b
  }

  return (
    <div className="space-y-4 credit-report">
      {(reportTitle || bureau) && (
        <Card>
          <CardHeader>
            {reportTitle && <CardTitle className="text-2xl">{reportTitle}</CardTitle>}
            {bureau && (
              <CardDescription className="text-base">
                Bureau: <span className="font-semibold">{getBureauLabel(bureau)}</span>
              </CardDescription>
            )}
          </CardHeader>
        </Card>
      )}
      <SubjectInfoSection report={report} />
      <ScoresSection report={report} />
      {evaluations && evaluations.length > 0 && (
        useServerEvaluations ? (
          <EvaluationsSectionServer evaluations={evaluations} />
        ) : (
          evaluationsComponent || null
        )
      )}
      <AddressesSection report={report} />
      <SummarySection report={report} />
      <TradesSection report={report} />
      <BankruptciesSection report={report} />
      <CollectionsSection report={report} />
    </div>
  )
}

function SubjectInfoSection({ report }: { report: EquifaxPrequalReport }) {
  const fullName = [
    report.subjectName?.firstName,
    report.subjectName?.middleName,
    report.subjectName?.lastName
  ].filter(Boolean).join(' ')

  return (
    <Card>
      <CardHeader>
        <CardTitle>Subject Information</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h4 className="font-medium text-sm text-gray-700 mb-1">Name</h4>
            <p className="text-sm">{fullName || 'N/A'}</p>
          </div>
          {report.birthDate && report.birthDate !== '[deleted]' && (
            <div>
              <h4 className="font-medium text-sm text-gray-700 mb-1">Birth Date</h4>
              <p className="text-sm">{report.birthDate}</p>
            </div>
          )}
          {report.identification?.subjectSocialNum && (
            <div>
              <h4 className="font-medium text-sm text-gray-700 mb-1">SSN</h4>
              <p className="text-sm font-mono">
                {report.identification.subjectSocialNum === '[deleted]' 
                  ? '[deleted]' 
                  : social(report.identification.subjectSocialNum)}
              </p>
            </div>
          )}
          {report.hitCode && (
            <div>
              <h4 className="font-medium text-sm text-gray-700 mb-1">Hit Code</h4>
              <Badge variant={report.isNoHit() ? "secondary" : "default"}>
                {report.hitCode.description || report.hitCode.code}
              </Badge>
            </div>
          )}
          {report.fraudVictimIndicator && (
            <div>
              <h4 className="font-medium text-sm text-gray-700 mb-1">Fraud Victim</h4>
              <Badge variant="outline">
                {report.fraudVictimIndicator.description || report.fraudVictimIndicator.code}
              </Badge>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function ScoresSection({ report }: { report: EquifaxPrequalReport }) {
  if (!report.models?.length) return null

  const maxScore = report.getMaxScore()

  return (
    <Card>
      <CardHeader>
        <CardTitle>Credit Scores</CardTitle>
      </CardHeader>
      <CardContent>
        <div>
          {report.models.map((model, idx) => (
            <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div>
                {model.score !== undefined && (
                  <div className="text-2xl font-bold text-blue-600">{model.score}</div>
                )}
                {model.rejects?.length > 0 && (
                  <div className="text-xs text-red-600 mt-1">
                    Rejects: {model.rejects.map(r => r.description).join(', ')}
                  </div>
                )}
                {model.reasons?.length > 0 && (
                  <div className="text-xs text-gray-600 mt-1">
                    {model.reasons.map((r, reasonIdx) => (
                      <div key={reasonIdx}>{r.description}</div>
                    ))}
                  </div>
                )}
              </div>
              {model.score === undefined && (
                <Badge variant="secondary">No Score</Badge>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

function SummarySection({ report }: { report: EquifaxPrequalReport }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Summary</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {report.monthlyDebt !== undefined && (
            <div>
              <h4 className="font-medium text-sm text-gray-700 mb-1">Monthly Debt</h4>
              <p className="text-lg font-semibold">{dollars(report.monthlyDebt)}</p>
            </div>
          )}
          {report.thirtySixtyNinetySummary && (
            <>
              <div>
                <h4 className="font-medium text-sm text-gray-700 mb-1">30 Day Late</h4>
                <p className="text-lg font-semibold">{report.thirtySixtyNinetySummary[30]}</p>
              </div>
              <div>
                <h4 className="font-medium text-sm text-gray-700 mb-1">60 Day Late</h4>
                <p className="text-lg font-semibold">{report.thirtySixtyNinetySummary[60]}</p>
              </div>
              <div>
                <h4 className="font-medium text-sm text-gray-700 mb-1">90 Day Late</h4>
                <p className="text-lg font-semibold">{report.thirtySixtyNinetySummary[90]}</p>
              </div>
            </>
          )}
          {report.latePaymentSummary && (
            <div className="md:col-span-3">
              <h4 className="font-medium text-sm text-gray-700 mb-2">Late Payment Summary (24 months)</h4>
              <div className="grid grid-cols-4 gap-2 text-xs">
                {Object.entries(report.latePaymentSummary).map(([code, count]) => {
                  if (count === 0) return null
                  const labels: Record<string, string> = {
                    '2': '30 days',
                    '3': '60 days',
                    '4': '90 days',
                    '5': '120+ days',
                    '6': 'Collection',
                    '7': 'Chapter 13',
                    '8': 'Repossession',
                    '9': 'Charge-off'
                  }
                  return (
                    <div key={code} className="flex justify-between p-2 bg-gray-50 rounded">
                      <span>{labels[code] || `Code ${code}`}:</span>
                      <span className="font-semibold">{count}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function TradesSection({ report }: { report: EquifaxPrequalReport }) {
  if (!report.trades?.length) return null

  return (
    <Card className="trades">
      <CardHeader>
        <CardTitle>Credit Accounts ({report.trades.length})</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {report.trades.map((trade, idx: number) => (
            <TradeAccount key={idx} trade={trade} />
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

function BankruptciesSection({ report }: { report: EquifaxPrequalReport }) {
  if (!report.bankruptcies?.length) return null

  return (
    <Card>
      <CardHeader>
        <CardTitle>Bankruptcies ({report.bankruptcies.length})</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {report.bankruptcies.map((bankruptcy, idx) => (
            <div key={idx} className="border rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-sm text-gray-700 mb-1">Type</h4>
                  <p className="text-sm">{bankruptcy.type}</p>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-gray-700 mb-1">Filer</h4>
                  <p className="text-sm">{bankruptcy.filer}</p>
                </div>
                {bankruptcy.dateFiled && (
                  <div>
                    <h4 className="font-medium text-sm text-gray-700 mb-1">Date Filed</h4>
                    <p className="text-sm">{mmddyyyy(bankruptcy.dateFiled)}</p>
                  </div>
                )}
                {bankruptcy.dispositionDate && (
                  <div>
                    <h4 className="font-medium text-sm text-gray-700 mb-1">Disposition Date</h4>
                    <p className="text-sm">{mmddyyyy(bankruptcy.dispositionDate)}</p>
                  </div>
                )}
                {bankruptcy.currentIntentOrDispositionCode && (
                  <div>
                    <h4 className="font-medium text-sm text-gray-700 mb-1">Current Status</h4>
                    <p className="text-sm">
                      {bankruptcy.currentIntentOrDispositionCode.description || 
                       bankruptcy.currentIntentOrDispositionCode.code}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

function CollectionsSection({ report }: { report: EquifaxPrequalReport }) {
  if (!report.collections?.length) return null

  return (
    <Card>
      <CardHeader>
        <CardTitle>Collections ({report.collections.length})</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {report.collections.map((collection, idx) => (
            <div key={idx} className="border rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-sm text-gray-700 mb-1">Client Name</h4>
                  <p className="text-sm">{collection.clientNameOrNumber}</p>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-gray-700 mb-1">Status</h4>
                  <Badge variant="outline">
                    {collection.statusCode?.description || collection.statusCode?.code}
                  </Badge>
                </div>
                {collection.dateOfFirstDelinquency && (
                  <div>
                    <h4 className="font-medium text-sm text-gray-700 mb-1">First Delinquency</h4>
                    <p className="text-sm">{mmddyyyy(collection.dateOfFirstDelinquency)}</p>
                  </div>
                )}
                {collection.dateReported && (
                  <div>
                    <h4 className="font-medium text-sm text-gray-700 mb-1">Date Reported</h4>
                    <p className="text-sm">{mmddyyyy(collection.dateReported)}</p>
                  </div>
                )}
                <div>
                  <h4 className="font-medium text-sm text-gray-700 mb-1">Original Amount</h4>
                  <p className="text-sm">{dollars(collection.originalAmount)}</p>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-gray-700 mb-1">Balance</h4>
                  <p className="text-sm font-semibold">{dollars(collection.balance)}</p>
                </div>
                {collection.creditorClassificationCode && (
                  <div>
                    <h4 className="font-medium text-sm text-gray-700 mb-1">Classification</h4>
                    <p className="text-sm">
                      {collection.creditorClassificationCode.description || 
                       collection.creditorClassificationCode.code}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

function AddressesSection({ report }: { report: EquifaxPrequalReport }) {
  if (!report.addresses?.length) return null

  return (
    <Card>
      <CardHeader>
        <CardTitle>Addresses ({report.addresses.length})</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Address</TableHead>
              <TableHead>City, State, ZIP</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>First Reported</TableHead>
              <TableHead>Last Reported</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {report.addresses.map((address, idx) => (
              <TableRow key={idx}>
                <TableCell>
                  <div className="text-sm font-medium">
                    {address.addressLine1}
                    {address.addressLine2 && (
                      <span className="block text-gray-600">{address.addressLine2}</span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    {address.cityName}, {address.stateAbbreviation} {address.zipCode}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">{address.addressType}</div>
                </TableCell>
                <TableCell>
                  <div className="text-sm text-gray-600">
                    {address.sourceOfAddress?.description || address.sourceOfAddress?.code || 'N/A'}
                  </div>
                </TableCell>
                <TableCell>
                  {address.dateFirstReported ? (
                    <div className="text-sm text-gray-600">{address.dateFirstReported}</div>
                  ) : (
                    <div className="text-sm text-gray-400">—</div>
                  )}
                </TableCell>
                <TableCell>
                  {address.dateLastReported ? (
                    <div className="text-sm text-gray-600">{address.dateLastReported}</div>
                  ) : (
                    <div className="text-sm text-gray-400">—</div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

